import {
  Box,
  Typography,
  Stack,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { useTenantConfig } from '../../../../hooks/use-tenant-config';
import { formatGrade } from '../../../../lib/grading';
import { CloseRounded, MenuBookRounded, EventNoteRounded } from '@mui/icons-material';
import { type AcademicRow, type EnrolledCourseDetail } from '../../../../hooks/use-reports';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

export interface StudentCoursesDialogProps {
  row: AcademicRow | null;
  onClose: () => void;
  isDark: boolean;
  /** Sin la columna de nota: con `graduation_audit` la nota numérica guardada
   *  son los puntos 4-3-2-1, no una nota de esta escala. */
  hideGrades?: boolean;
}

export function courseStatusChip(status: string, t: TFunction) {
  const colors: Record<string, 'primary' | 'success' | 'error' | 'default' | 'warning'> = {
    enrolled: 'primary',
    approved: 'success',
    failed: 'error',
    dropped: 'default',
  };
  return (
    <Chip
      size="small"
      // Solo lo que tiene clave: en producción una clave que falta se reemplaza
      // por un aviso genérico, aun con `defaultValue`.
      label={status in colors ? t(`reports.studentCourses.status.${status}`) : status}
      color={colors[status] ?? 'default'}
      variant="outlined"
      sx={{ fontSize: 11, height: 22 }}
    />
  );
}

export function StudentCoursesDialog({
  row,
  onClose,
  isDark,
  hideGrades = false,
}: StudentCoursesDialogProps) {
  const { t } = useTranslation('sis');
  const { gradingScale } = useTenantConfig();
  const courses: EnrolledCourseDetail[] = row?.enrolledCourses ?? [];

  // Group by term for display, then sort groups chronologically and courses within each group by code
  const grouped = courses.reduce<Record<string, EnrolledCourseDetail[]>>((acc, c) => {
    const key = c.termName || t('reports.studentCourses.noTerm');
    acc[key] = [...(acc[key] ?? []), c];
    return acc;
  }, {});

  // Sort entries: terms by start date (ascending), courses within each term by section start date then code
  const sortedEntries = Object.entries(grouped)
    .sort(([, aItems], [, bItems]) => {
      const aDate = aItems[0]?.termStartDate ?? '';
      const bDate = bItems[0]?.termStartDate ?? '';
      return aDate.localeCompare(bDate);
    })
    .map(
      ([termName, items]) =>
        [
          termName,
          [...items].sort((a, b) => {
            // Primary sort: section start date
            const dA = a.sectionStartDate ?? '';
            const dB = b.sectionStartDate ?? '';
            if (dA && dB) return dA.localeCompare(dB);
            if (dA) return -1;
            if (dB) return 1;
            // Fallback: course code
            return a.courseCode.localeCompare(b.courseCode);
          }),
        ] as [string, EnrolledCourseDetail[]],
    );

  return (
    <Dialog
      open={row !== null}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: isDark ? 'rgba(15,15,25,0.97)' : 'background.paper',
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
      >
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Box sx={{ color: 'primary.main', display: 'flex' }}>
            <MenuBookRounded />
          </Box>
          <Box>
            <Typography fontWeight={800} variant="h6">
              {row?.name || '—'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row?.programName !== '—' ? row?.programName : t('reports.studentCourses.noProgram')}
              {' · '}
              {t('reports.studentCourses.courseCount', { count: courses.length })}
            </Typography>
          </Box>
        </Stack>
        <IconButton size="small" onClick={onClose}>
          <CloseRounded />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {courses.length === 0 ? (
          <Box sx={{ py: 8, textAlign: 'center', color: 'text.secondary' }}>
            <MenuBookRounded sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
            <Typography>{t('reports.studentCourses.empty')}</Typography>
          </Box>
        ) : (
          sortedEntries.map(([termName, items]) => (
            <Box key={termName}>
              {/* Term header */}
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                px={3}
                py={1}
                sx={{
                  bgcolor: isDark ? 'rgba(99,102,241,0.08)' : 'rgba(99,102,241,0.04)',
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                <EventNoteRounded sx={{ fontSize: 15, color: 'primary.main' }} />
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="primary.main"
                  sx={{ textTransform: 'uppercase', letterSpacing: '0.07em', fontSize: 11 }}
                >
                  {termName}
                </Typography>
              </Stack>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        '& th': {
                          color: 'text.secondary',
                          fontWeight: 600,
                          fontSize: 11,
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                        },
                      }}
                    >
                      <TableCell sx={{ pl: 3 }}>
                        {t('reports.studentCourses.columns.course')}
                      </TableCell>
                      <TableCell>{t('reports.studentCourses.columns.code')}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        {t('reports.studentCourses.columns.section')}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {t('reports.studentCourses.columns.dates')}
                      </TableCell>
                      <TableCell align="center">
                        {t('reports.studentCourses.columns.status')}
                      </TableCell>
                      {!hideGrades && (
                        <TableCell align="right" sx={{ pr: 3 }}>
                          {t('reports.studentCourses.columns.grade')}
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((c) => (
                      <TableRow
                        key={c.enrollmentId}
                        sx={{ '&:last-child td': { borderBottom: 0 } }}
                      >
                        <TableCell sx={{ pl: 3 }}>
                          <Typography fontSize={13} fontWeight={500}>
                            {c.courseName}
                          </Typography>
                          <Typography fontSize={11} color="text.secondary">
                            {c.programName !== '—' ? c.programName : ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {c.courseCode}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                          <Typography variant="body2" color="text.secondary">
                            {c.sectionCode}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          {c.sectionStartDate ? (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ whiteSpace: 'nowrap' }}
                            >
                              {c.sectionStartDate}
                              {c.sectionEndDate && <> &ndash; {c.sectionEndDate}</>}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.disabled">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">{courseStatusChip(c.status, t)}</TableCell>
                        {!hideGrades && (
                          <TableCell align="right" sx={{ pr: 3 }}>
                            <Typography
                              fontWeight={600}
                              fontSize={13}
                              color={
                                c.grade !== undefined || c.gradeLabel
                                  ? 'text.primary'
                                  : 'text.disabled'
                              }
                            >
                              {formatGrade({ value: c.grade, label: c.gradeLabel }, gradingScale)}
                            </Typography>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ))
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'flex-end' }}>
        <Button variant="outlined" size="small" onClick={onClose}>
          {t('common:close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
